import React, {useCallback, useState, useEffect} from 'react'
import Markdown from 'marked-react'
import styled from 'styled-components'
import {
  OBIE_NEW_LINE,
  OBIE_RESPONSE_SPLITER,
} from 'organization/Obie/ObieServiceProvider'
import ChatIcon from 'organization/Obie/Blocks/ProcessForm/ChatIcon'

const TYPE_WRITER_SPEED = 15

export default function TypeWriter(props: {
  text: string
  onFinish: () => void
}) {
  const {text, onFinish} = props
  const [showSecond, setShowSecond] = useState<boolean>(false)

  let processedText = (text || '').replaceAll(OBIE_NEW_LINE, '\n')

  const textParts = processedText.split(OBIE_RESPONSE_SPLITER)

  const pauseThenRenderSecond = useCallback(() => {
    const timer = setTimeout(() => setShowSecond(true), 1000)

    return () => {
      clearTimeout(timer)
    }
  }, [setShowSecond])

  return (
    <TypedText>
      <HiddenMarkdown
        text={textParts[0]}
        onFinish={pauseThenRenderSecond}
        showing={true}
      />
      <HiddenMarkdown
        text={textParts[1]}
        onFinish={onFinish}
        showing={showSecond}
      />
    </TypedText>
  )
}

const HiddenMarkdown = (props: {
  text: string
  onFinish: () => void
  showing: boolean
}) => {
  const {text, onFinish, showing} = props
  const {displayText, finished} = useTypewriter(text, onFinish, showing)

  if (!showing) {
    return null
  }

  return (
    <>
      <Markdown breaks gfm>
        {displayText}
      </Markdown>
      {!finished && text && <ChatIcon />}
    </>
  )
}

const useTypewriter = (
  text: string,
  onFinish: () => void,
  showing: boolean,
) => {
  const [displayText, setDisplayText] = useState('')
  const [finished, setFinished] = useState(false)

  useEffect(() => {
    if (finished || !showing || !text) {
      return
    }

    let i = 0
    setDisplayText('')
    setFinished(false)
    const typingInterval = setInterval(() => {
      if (i < text.length) {
        setDisplayText((prev) => prev + text.charAt(i))
        i += 1
      }

      if (i >= text.length) {
        clearInterval(typingInterval)
        setFinished(true)
        onFinish()
      }
    }, TYPE_WRITER_SPEED)

    return () => {
      clearInterval(typingInterval)
      onFinish()
    }
  }, [text, finished, onFinish, showing])

  return {displayText, finished}
}

const TypedText = styled.div`
  font-size: 1.25rem;
  font-weight: normal;
  color: white;
  margin-bottom: ${(props) => props.theme.spacing[4]};
  line-height: 1.5;
  text-align: left;
  width: 100%;
`
